import React from "react";
import { Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import ProfileRecovery from "components/LM/SocialMediaProfile/ProfileRecovery";
import NumeralMetric from "components/LM/common/NumeralMetric";
import BssoPermissions from "components/LM/Bsso/Permissions";
import DateFormat from "components/LM/common/DateFormat";
import ProfileImage from "components/LM/common/ProfileImage";
import ProxyImage from "components/LM/common/ProxyImage";
import CrawlingSwitch from "components/LM/SocialMediaProfile/CrawlingSwitch";
import InstagramOfficialCrawlingSwitch from "components/LM/SocialMediaProfile/InstagramOfficialCrawlingSwitch";
import InstagramStoryCrawlingSwitch from "components/LM/SocialMediaProfile/InstagramStoryCrawlingSwitch";
import Badge from 'react-bootstrap/Badge';
import dayjs from "dayjs";
import ProfileLabelsForm from "components/LM/SocialMediaProfile/ProfileLabelsForm";

const VOICE_CUSTOMER_ID = 1;

const ORPHANS_CUSTOMER_ID = 4;

function ProfileDetails(props) {
	const { type, typeName, profile, audienceLabel, lastPostDate, showExtendedInfo, usePictureUrl } = props;
	const profileName = profile.info.name || profile.info.screen_name;

	let profileImage;
	if (usePictureUrl) {
		profileImage = <ProxyImage src={profile.info.picture_url} alt={profileName} className="img-fluid rounded-circle mb-2" width="100" height="100" />
	} else {
		profileImage = <ProfileImage id={profile.info.id} type={type} alt={profileName} className="img-fluid rounded-circle mb-2" width="100" height="100" />
	}

	const basicInfo = (<CardBody className="text-center">
		{profileImage}
		<CardTitle tag="h5" className="mb-0">{profileName}</CardTitle>
		<div className="text-muted mb-2">{profile.info.description}</div>
		<div><Reach type={type} audience={profile.info.audience} audienceLabel={audienceLabel} following={profile.info.following} /></div>
		<RecoverButton type={type} typeName={typeName} profile={profile} />
	</CardBody>);

	let extendedInfo;
	if (showExtendedInfo) {
		const profileDetailsBadges = [];
		if (type === 'instagram') {
			profileDetailsBadges.push(
				<Badge key={`${profile.info.id}_business`} bg="info">{`${profile.info.is_business ? "Is" : "Not"} business`}</Badge>
			);
			if (profile.info.is_restricted) {
				profileDetailsBadges.push(
					<Badge key={`${profile.info.id}_restricted`} bg="warning" title={profile.info.restricted_reason} color="warning">Restricted</Badge>
				);
			}
		}

		if (profile.info.api_errors_counter) {
			profileDetailsBadges.push(
				<ApiErrorCounter key="profile_counter" counter={profile.info.api_errors_counter} origin="profile" />
			);
		}

		let profileDetailsSchedulerElement;
		if (profile.scheduler) {
			const isSchedulerOutdated = dayjs(profile.scheduler.next_scheduled_at).isBefore(dayjs());
			profileDetailsSchedulerElement = <div className={isSchedulerOutdated ? 'text-warning' : null}>Next scheduled at: <DateFormat>{profile.scheduler.next_scheduled_at}</DateFormat></div>;
		}

		let profileLabelsForm;
		if (!profile.voice && ["instagram", "tiktok"].includes(type)) {
			profileLabelsForm = (
				<div>Labels: <ProfileLabelsForm profile={profile.info} type={type} /></div>
			);
		}

		extendedInfo = (
			<>
				<CardBody>
					<CardTitle tag="h5">Profile Details</CardTitle>
					<div>ID: {profile.info.id}</div>
					{profile.info.sid && <div>SID: {profile.info.sid}</div>}
					<div>Inserted at: <DateFormat>{profile.info.inserted_at}</DateFormat></div>
					<div>Last updated at: <DateFormat>{profile.info.updated_at}</DateFormat></div>
					{profileDetailsSchedulerElement}
					{
						!profileDetailsBadges.length ? null : <><div>Status:</div>
							<div className="mt-1 text-center">
								{profileDetailsBadges}
							</div></>
					}
					{profileLabelsForm}
				</CardBody>
				<hr className="my-0" />
				<CrawlingCustomerDetails type={type} profile={profile} lastPostDate={lastPostDate} />
			</>
		);
	}

	let customerCrawlingSwitch;

	let checked = false;
	if (profile.crawling) {
		checked = Boolean(profile.crawling.customers.some(customer => customer.id === VOICE_CUSTOMER_ID || customer.id === ORPHANS_CUSTOMER_ID));
	}

	const isOrphan = Boolean(!profile.voice);

	customerCrawlingSwitch = (
		<CrawlingSwitch
			checked={checked}
			isOrphan={isOrphan}
			voiceId={profile.voice ? profile.voice.id : null}
			profileId={profile.info.id}
			type={type}
			className="float-right btn-sm ml-2 custom-gray-border" />
	);

	return (
		<Card>
			<CardHeader>
				<CardTitle tag="h5" className="mb-0 capitalize">{formattedType(type)} Basic Info
					{customerCrawlingSwitch}
				</CardTitle>
			</CardHeader>
			{basicInfo}
			<hr className="my-0" />
			{extendedInfo}
		</Card>
	);
}

function CrawlingCustomerDetails({ type, profile, lastPostDate }) {

	let customerCrawling;
	let customerCrawlingControl;
	let customerLastPostRetrieved;
	if (profile.crawling) {
		if (profile.crawling.customers.length) {
			customerCrawling = (
				<>
					<div>Customers:</div>
					<div className="mt-1 text-center">
						{
							profile.crawling.customers.map(customer => {
								const name = customer.name.replace(/^[A-Z]+\s/, "");
								return <Badge key={customer.id} bg="info">{name}</Badge>
							})
						}
					</div>
				</>
			);
		}

		customerCrawlingControl = <CrawlingControl control={profile.crawling.control} />;

		if (lastPostDate === undefined) {
			customerLastPostRetrieved = null;
		} else if (lastPostDate) {
			customerLastPostRetrieved = <div>Last post retrieved at: <DateFormat>{lastPostDate}</DateFormat></div>;
		} else {
			customerLastPostRetrieved = <span className="text-danger">This profile has not published in over 4 years or more</span>;
		}
	}

	return (
		<CardBody>
			<CardTitle tag="h5">
				Crawling Details
			</CardTitle>
			{customerLastPostRetrieved}
			{customerCrawling}
			{customerCrawlingControl}
			{type === 'instagram' && profile.voice && profile.crawling?.customers?.length > 0 && (
				<InstagramOfficialCrawlingSwitch
					voiceId={profile.voice.id}
					profileId={profile.info.id}
				/>
			)}
			{type === 'instagram' && (
				<InstagramStoryCrawlingSwitch
					profileId={profile.info.id}
				/>
			)}
		</CardBody>
	);
}

function RecoverButton({ type, typeName, profile }) {
	const permissions = ["social_recovery"];
	if (type === "twitter") {
		permissions.push("twitter_recovery");
	}

	return <BssoPermissions permissions={permissions}>
		<ProfileRecovery type={type} typeName={typeName} profile={profile} canRecover={profile.recovery.can_recover}></ProfileRecovery>
	</BssoPermissions>
}

function Reach({ type, audience, audienceLabel, following }) {

	const reach = [<span key={type + 'audience'}>{audienceLabel}: <NumeralMetric>{audience}</NumeralMetric> </span>];
	if (following) {
		reach.push(<span key={type + 'following'}>following: <NumeralMetric>{following}</NumeralMetric></span>)
	}
	return reach;
}

function ApiErrorCounter({ counter, origin }) {
	if (!counter) return null;

	let title;
	let message;
	if (origin === 'profile') {
		title = `${counter} errors trying to update the profile data`;
		message = `${counter} profile update errors`;
	} else if (origin === 'crawling') {
		title = `${counter} errors trying to retrieve profile posts`;
		message = `${counter} crawling errors`;
	}

	return <Badge key={`${origin}_counter`} title={title} variant="warning">{message}</Badge>
}

function CrawlingControl({ control }) {

	if (control.length === 1) {
		const { last_capture_at, next_capture_at, error_counter } = control[0];
		return <CrawlingDetails lastCaptureAt={last_capture_at} nextCaptureAt={next_capture_at} errorCounter={error_counter} />;
	}

	return control.map((controlItem, index) => (
		<div className="mt-4" key={index}>
			<CardTitle tag="h3">
				{controlItem.type.charAt(0).toUpperCase() + controlItem.type.slice(1)} Details
			</CardTitle>
			<CrawlingDetails lastCaptureAt={controlItem.last_capture_at} nextCaptureAt={controlItem.next_capture_at} errorCounter={controlItem.error_counter} />
		</div>
	));
}

function CrawlingDetails({ lastCaptureAt, nextCaptureAt, errorCounter }) {
	return (
		<>
			<div>Last API call at: <DateFormat>{lastCaptureAt}</DateFormat></div>
			<div>Next API call at: <DateFormat>{nextCaptureAt}</DateFormat></div>
			{errorCounter !== undefined && (
				// Prevent crash app until merge BIT-2693
				<div className="mt-1 text-center">
					<ApiErrorCounter key="crawling_counter" counter={errorCounter} origin="crawling" />
				</div>
			)}
		</>
	);
}

function formattedType(type) {
    if (type === 'tiktok') {
        return 'TikTok';
    } else if (type === 'wechat_public') {
        return 'Wechat';
    }
    return type;
}

export default ProfileDetails;
